import { useApolloClient } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  HStack,
  Link,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Switch } from '@gamma/ui/form-fields';
import { Alert, Modal } from '@gamma/ui/overlay';
import { LoadingSpinner } from '@gamma/ui/progress';
import { URLS } from '@investigator/shared/constants';
import { i18n } from '@investigator/shared/localization';
import {
  AuthContext,
  broadcastCrossTabEvent,
} from '@investigator/shared/providers';
import { useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { safeParse } from 'secure-json-parse';
import {
  GET_TENANT_PRIVILEGES,
  useUpdatePrivileges,
} from '../../../admin/queries';

const { integrations } = i18n.pages;
const { enabled, disabled, success, save, cancel } = integrations;

const {
  warning,
  warningDetails,
  whenYouEnableGPT,
  whenYouEnableGPTPrivateData,
  GPTIntegration,
  chatGPTOnChangeToastDescription,
  privateData,
  nonPrivateData,
  learnMore,
  model,
  modelValue,
  dataSharing,
  dataSharingValue,
  dataUsage,
  dataUsageValue,
} = integrations.chatGPT;

interface ChatGPTSettingsCardModalProps {
  isOpen: boolean;
  isPrivateDataCard: boolean;
  isEnabled?: boolean;
  onClose: () => void;
}

const { GPT_DOCS } = URLS;

export const ChatGPTSettingsCardModal = ({
  isOpen,
  onClose,
  isPrivateDataCard,
}: ChatGPTSettingsCardModalProps) => {
  const showToast = useToast();
  const { userLicense, setUserPrivileges, userPrivileges } =
    useContext(AuthContext);
  const { tenant = '' } = userLicense ?? {};
  const { chatgpt_private_data, chatgpt } =
    userPrivileges?.[tenant ?? ''] ?? {};

  const isEnabled = isPrivateDataCard ? chatgpt_private_data : chatgpt;
  const privilege = isPrivateDataCard ? 'chatgpt_private_data' : 'chatgpt';

  const { reset, handleSubmit, control, watch } = useForm({
    mode: 'onChange',
    defaultValues: {
      chatgpt: isEnabled,
    },
  });

  const formData = watch();

  const client = useApolloClient();
  const [updatePrivileges, { loading: updatePrivilegesLoading }] =
    useUpdatePrivileges({
      variables: {
        input: {
          [privilege]: formData.chatgpt,
        },
      },
      onCompleted: async () => {
        const toastDescription = i18n.formatString(
          chatGPTOnChangeToastDescription,
          isPrivateDataCard ? privateData : nonPrivateData,
          formData.chatgpt ? enabled : disabled,
        );
        if (tenant) {
          const {
            data: { getTenantPrivileges },
          } = await client.query({
            query: GET_TENANT_PRIVILEGES,
            variables: { tenant_id: tenant },
            fetchPolicy: 'network-only',
          });
          const tenantPrivileges = safeParse(getTenantPrivileges);
          const newUserPrivileges = { ...userPrivileges };
          // Merge privileges instead of replacing to avoid losing other providers
          newUserPrivileges[tenant] = {
            ...(userPrivileges?.[tenant] ?? {}),
            ...tenantPrivileges,
          };
          setUserPrivileges(newUserPrivileges);
          reset({ chatgpt: newUserPrivileges[tenant][privilege] });

          // Broadcast the privilege changes to other tabs
          broadcastCrossTabEvent({
            type: 'privileges-updated',
            data: {
              tenantId: tenant,
              privileges: tenantPrivileges,
            },
          });
        }
        showToast({
          title: success,
          description: toastDescription,
          status: 'success',
          isClosable: true,
          position: 'bottom-right',
        });
      },
    });

  const handleOnClose = () => {
    reset();
    onClose();
  };

  const handleSubmitGPTStatus = async () => {
    await updatePrivileges();
    onClose();
  };

  return (
    <Modal
      size="lg"
      isCentered
      isOpen={isOpen}
      onClose={handleOnClose}
      title={`${GPTIntegration} (${isPrivateDataCard ? privateData : nonPrivateData})`}
      closeOnEsc={!updatePrivilegesLoading}
      isCloseDisabled={updatePrivilegesLoading}
      closeOnOverlayClick={!updatePrivilegesLoading}
      body={
        <VStack
          as="form"
          spacing={4}
          alignItems="flex-start"
          onSubmit={handleSubmit(handleSubmitGPTStatus)}
        >
          <Text>
            {isPrivateDataCard ? whenYouEnableGPTPrivateData : whenYouEnableGPT}{' '}
            <Link isExternal href={GPT_DOCS} color={'blue.300'}>
              {learnMore}
            </Link>
          </Text>
          <HStack alignItems="start">
            <Text textColor="gray" mr={2} minW={32}>
              {model}
            </Text>
            <Text>{modelValue}</Text>
          </HStack>
          <HStack alignItems="start">
            <Text textColor="gray" mr={2} minW={32}>
              {dataSharing}
            </Text>
            <Text>{dataSharingValue}</Text>
          </HStack>
          <HStack alignItems="start">
            <Text textColor="gray" mr={2} minW={32}>
              {dataUsage}
            </Text>
            <Text>{dataUsageValue}</Text>
          </HStack>
          <HStack spacing={2} alignItems="center">
            {updatePrivilegesLoading ? (
              <LoadingSpinner size="sm" />
            ) : (
              <Controller
                name="chatgpt"
                control={control}
                render={({ field: { onChange, name, value } }) => (
                  <Switch
                    inline
                    name={name}
                    isChecked={value}
                    onChange={onChange}
                    data-testid="exportSwitch"
                    isDisabled={updatePrivilegesLoading}
                    label={formData.chatgpt ? enabled : disabled}
                  />
                )}
              />
            )}
          </HStack>
          <Alert variant="subtle" status="warning">
            <Text textStyle="h6" display="block">
              {warning}
            </Text>
            <Text>{warningDetails}</Text>
          </Alert>
          <ButtonGroup pb={2} w="100%" justifyContent="end">
            <Button
              variant="solid"
              colorScheme="gray"
              onClick={handleOnClose}
              isDisabled={updatePrivilegesLoading}
            >
              {cancel}
            </Button>
            <Button
              type="submit"
              variant="solid"
              isDisabled={isEnabled === formData.chatgpt}
              isLoading={updatePrivilegesLoading}
            >
              {save}
            </Button>
          </ButtonGroup>
        </VStack>
      }
    />
  );
};
